import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import HubCountryLinks from "@/components/HubCountryLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// EN mirror of /marriage — marriage registration for foreigners in Thailand
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { Heart, FileCheck2, Building2 } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Marry in Thailand — Foreigner's Document Pack | NYC Language Institute";
const DESC =
  "Marriage in Thailand as a foreigner: affirmation of freedom to marry, embassy translations. Get your timeline and checklist in minutes — chat now.";

const STEPS = [
  { n: 1, t: "Affirmation of Freedom to Marry", d: "Issued by your embassy in Bangkok. We book the appointment and prepare the supporting documents." },
  { n: 2, t: "Translate to Thai", d: "Sworn translation of the affirmation into Thai — required by every Thai district office." },
  { n: 3, t: "MFA legalization", d: "MFA Department of Consular Affairs stamps the Thai translation. 1–3 working days." },
  { n: 4, t: "Amphur registration", d: "We accompany you to the district office (amphur) for the actual marriage registration." },
  { n: 5, t: "Marriage certificate translation", d: "Translate the Thai marriage certificate back into your language + apostille for home country recognition." },
];

const NATIONALITIES = [
  "united-states", "united-kingdom", "germany", "france", "italy", "spain", "netherlands",
  "australia", "new-zealand", "canada", "japan", "south-korea", "china", "india",
];

export const Route = createFileRoute("/en/marriage/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("marriage", "en", "https://nyclanguageinstitute.com/en/marriage") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/marriage", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/marriage", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/marriage", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/marriage", "en")},
      { property: "og:url", content: `${SITE}/en/marriage` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/marriage` },
      ...hreflangLinks("/marriage", "/en/marriage"),
    ],
    scripts: [speakableSchema(), {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Marry in Thailand as a Foreigner",
        inLanguage: "en",
        step: STEPS.map((s) => ({ "@type": "HowToStep", position: s.n, name: s.t, text: s.d })),
      }),
    },
    entitySameAsSchema(),
    softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Thai marriage registration tooling for 50+ nationalities." }),
    claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`${SITE}/en/marriage`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnMarriagePage,
});

function EnMarriagePage() {
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-5xl px-4 pt-6">
        <AIAnswerBox
          question="How do foreigners register a marriage in Thailand?"
          answer="Foreigners need an Affirmation of Freedom to Marry from their embassy in Bangkok, then a sworn Thai translation, MFA legalization, and registration at any amphur (district office). NYC handles all 5 steps end-to-end in 5–7 working days."
          stats={[
            { label: "Steps", value: "5" },
            { label: "Timeline", value: "5–7 days" },
            { label: "Nationalities", value: "50+" },
            { label: "Hours", value: "Mon–Sat 09:00–18:00" },
          ]}
          sourceUrl={`${SITE}/en/marriage`}
          lang="en"
        />
      </div>
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Marry in Thailand — <span className="text-amber-300">end-to-end document pack</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Every step from your embassy affirmation to the amphur registration and home-country apostille.
            We've helped 1,200+ couples register legally since 2007.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">How can a foreigner legally marry in Thailand?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Foreigners must first obtain an Affirmation of Freedom to Marry from their embassy in Bangkok, then translate it to Thai, certify it at the MFA, and register at a Thai district office (amphur). Total cost runs THB 15,000–35,000 and takes 5–10 business days. NYC handles all steps including post-marriage apostille for home-country recognition.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold text-navy-deep">5-step workflow</h2>
          <ol className="mt-6 space-y-4">
            {STEPS.map((s) => (
              <li key={s.n} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-navy-deep">{s.n}</span>
                <div>
                  <h3 className="text-base font-bold text-navy-deep">{s.t}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold text-navy-deep flex items-center gap-2">
            <Heart className="h-6 w-6 text-amber-600" /> Per-nationality checklists
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {NATIONALITIES.map((n) => (
              <Link key={n} to="/marriage/$nationality" params={{ nationality: n }}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-amber-400">
                <Building2 className="h-4 w-4 text-amber-600" />
                <span className="font-medium capitalize text-navy-deep">{n.replace(/-/g, " ")}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          <FileCheck2 className="h-5 w-5 text-amber-600" />
          <p className="mt-2">
            Typical fee range ฿15,000–฿35,000 depending on embassy + apostille destinations. Request a quote at{" "}
            <Link to="/en/quote" className="font-bold text-amber-700 hover:underline">/en/quote</Link>.
          </p>
        </div>
      </section>
          <QuotableStatsStrip cluster="marriage" locale="en" variant="light" />
          <HubCountryLinks service="marriage-abroad" lang="en" />
          <ServicePageEnhancer serviceName="Marriage in Thailand" locale="en" />
<RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
